<script setup>
import { ref, watch } from 'vue'
import { scheduleDraft, scheduleErrors } from '@/utils/dispatchScheduleForm.js'

const props = defineProps({ kind: String, source: Object, timezone: { type: String, default: 'Asia/Shanghai' },
  assets: { type: Array, default: () => [] }, assignments: { type: Array, default: () => [] }, busy: Boolean, pending: Boolean,
  fieldErrors: { type: Object, default: () => ({}) } })
const emit = defineEmits(['submit', 'cancel'])
const draft = ref(scheduleDraft(props.kind))
const errors = ref({})
const previewFailures = ref([])
function selectedAsset(index) { return props.assets.find(asset => asset.id === draft.value.material_ids[index]) }
const days = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
watch(() => props.source, source => {
  draft.value = scheduleDraft(props.kind, { timezone: props.timezone, ...(source || {}) })
  errors.value = {}
}, { immediate: true })
function validate() { errors.value = scheduleErrors(draft.value) }
watch(() => props.fieldErrors, value => { errors.value = { ...errors.value, ...(value || {}) } })
function toggleDay(day) {
  const selected = draft.value.weekdays
  draft.value.weekdays = selected.includes(day) ? selected.filter(value => value !== day) : [...selected, day]
  validate()
}
function toggleTarget(id) {
  const selected = draft.value.targets
  draft.value.targets = selected.some(value => value.app_binding_id === id)
    ? selected.filter(value => value.app_binding_id !== id) : [...selected, { app_binding_id: id }]
  validate()
}
function submit() {
  validate()
  if (!Object.keys(errors.value).length && !props.busy && !props.pending) emit('submit', scheduleDraft(props.kind, draft.value))
}
</script>

<template>
  <form class="schedule-editor" @submit.prevent="submit" @change="validate">
    <fieldset class="editor-fields" :disabled="busy || pending">
    <label>日程名称 <input v-model="draft.name" maxlength="80" placeholder="例如：午后分享" :disabled="busy"></label>
    <label>项目时区 <input v-model="draft.timezone" required :disabled="busy" :aria-invalid="Boolean(errors.timezone)">
      <span v-if="errors.timezone" class="field-error">{{ errors.timezone }}</span>
    </label>
    <template v-if="kind === 'periodic'">
      <label>下发频率 <select v-model="draft.frequency" :disabled="busy"><option value="daily">每天</option><option value="weekly">每周</option><option value="monthly">每月</option></select></label>
      <fieldset v-if="draft.frequency === 'weekly'"><legend>每周哪几天 <b>必选</b></legend>
        <label v-for="(day, index) in days" :key="day" class="choice"><input type="checkbox" :checked="draft.weekdays.includes(index)" :disabled="busy" @change="toggleDay(index)">{{ day }}</label>
        <p v-if="errors.weekdays" class="field-error">{{ errors.weekdays }}</p>
      </fieldset>
      <template v-if="draft.frequency === 'monthly'">
        <label>每月几号 <input v-model.number="draft.month_day" type="number" min="1" max="31" required :disabled="busy">
          <span v-if="errors.month_day" class="field-error">{{ errors.month_day }}</span>
        </label>
        <label>当月没有这一天 <select v-model="draft.short_month_policy" :disabled="busy"><option value="last_day">当月最后一天执行</option><option value="skip">跳过当月</option></select></label>
      </template>
      <label>下发时间 <b>必填</b><input v-model="draft.local_time" type="time" required :disabled="busy">
        <span v-if="errors.local_time" class="field-error">{{ errors.local_time }}</span>
      </label>
      <p class="hint">每次由系统从当前可用素材中随机选择两张图片和对应说明。</p>
    </template>
    <template v-else>
      <label>下发日期和时间 <b>必填</b><input v-model="draft.once_local" type="datetime-local" required :disabled="busy" :aria-invalid="Boolean(errors.once_local)">
        <span v-if="errors.once_local" class="field-error">{{ errors.once_local }}</span>
      </label>
      <label>本次图片 <select v-model="draft.selection_mode" :disabled="busy"><option value="automatic_two">由系统选择两张</option><option value="explicit_two">我预选两张</option></select></label>
      <fieldset v-if="draft.selection_mode === 'explicit_two'"><legend>预选两张图片 <b>必选</b></legend>
        <label v-for="index in [0, 1]" :key="index">图片 {{ index + 1 }}
          <select v-model="draft.material_ids[index]" :disabled="busy" required><option value="">请选择图片</option>
            <option v-for="asset in assets" :key="asset.id" :value="asset.id">{{ asset.name || asset.file_name || '素材图片' }}</option>
          </select>
          <img v-if="selectedAsset(index)?.thumbnail_url && !previewFailures.includes(draft.material_ids[index])"
            :src="selectedAsset(index).thumbnail_url" :alt="`预选图片 ${index + 1}`" loading="lazy"
            @error="previewFailures.push(draft.material_ids[index])">
          <small v-else-if="draft.material_ids[index]">图片预览暂时不可用，保存时系统会验证实际图片。</small>
          <span class="description" v-if="selectedAsset(index)">{{ selectedAsset(index).enterprise_description || selectedAsset(index).metadata?.enterprise_description || '暂无说明，请先到素材库补充。' }}</span>
        </label>
        <p v-if="errors.material_ids" class="field-error">{{ errors.material_ids }}</p>
      </fieldset>
    </template>
    <label>接收范围 <select v-model="draft.target_scope" :disabled="busy"><option value="all_active_bindings">执行时所有已绑定手机</option><option value="exact_targets">指定手机</option></select></label>
    <fieldset v-if="draft.target_scope === 'exact_targets'"><legend>接收手机 <b>必选</b></legend>
      <label v-for="assignment in assignments" :key="assignment.id" class="choice">
        <input type="checkbox" :checked="draft.targets.some(target => target.app_binding_id === assignment.id)" :disabled="busy" @change="toggleTarget(assignment.id)">
        {{ assignment.persona_name || assignment.name || `手机 ${assignment.app_slot_no || ''}` }}
      </label>
      <p v-if="!assignments.length" class="hint">目前没有已绑定手机；可以先保存全部手机范围的日程。</p>
      <p v-if="errors.targets" class="field-error">{{ errors.targets }}</p>
    </fieldset>
    <label class="choice"><input v-model="draft.enabled" type="checkbox" :disabled="busy">启用本条日程</label>
    </fieldset>
    <footer><button type="submit" :disabled="busy || pending">{{ pending && !busy ? '结果待确认' : (busy ? '正在保存…' : '保存日程') }}</button><button type="button" :disabled="busy || pending" @click="emit('cancel')">取消</button></footer>
  </form>
</template>

<style scoped>
.schedule-editor { display: grid; gap: 14px; padding: 18px; border: 1px solid #c8e1df; border-radius: 14px; background: #f6fbfb; }
.editor-fields { display: grid; gap: 14px; padding: 0; border: 0; margin: 0; }
label { display: grid; gap: 7px; color: #315760; font-weight: 700; }
input, select { box-sizing: border-box; min-width: 0; width: 100%; padding: 10px; border: 1px solid #c8dcdf; border-radius: 8px; background: white; font: inherit; }
fieldset { min-width: 0; padding: 12px; border: 1px solid #c8dcdf; border-radius: 10px; }
legend, b { color: #9a5d10; font-size: 13px; }
.choice { display: inline-flex; align-items: center; gap: 8px; margin: 5px 12px 5px 0; }
.choice input { width: 18px; height: 18px; }
.hint { margin: 0; color: #607f88; line-height: 1.6; }
img { width: min(100%, 240px); max-height: 220px; object-fit: contain; border-radius: 8px; }
.description { max-height: 130px; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; font-weight: 400; font-size: 14px; }
.field-error { color: #a3342c; font-size: 13px; font-weight: 600; }
footer { display: flex; flex-wrap: wrap; gap: 10px; }
button { padding: 10px 16px; border: 1px solid #81bcb7; border-radius: 9px; color: #08736b; background: white; font: inherit; font-weight: 800; }
button[type=submit] { color: white; background: #087c73; }
button:disabled { opacity: .55; }
</style>
